import React, { useMemo } from 'react';
import { AccommodationsContext, AccommodationsContextProps } from './Accommodations.context';
import { AccommodationPlaceType, TravelReservationBookingDetailsQuery, useAccommodationHotelsQuery, useTravelReservationBookingDetailsQuery } from '@graphql/generated';
import { useAuth } from '@shared/components/AuthProvider';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useAccommodationPlacePhotos } from '@shared/utils/accommodations';
import { useEventInfo } from '@shared/utils/eventInfo';
import { normalizeWithFallback } from '@shared/utils/normalizeWithFallback';
import { bookingInfoFallback, bookingReservationFallback, joyPlaceFallback } from '../components/GuestHotelBookingsList/GuestHotelBookings.constants';
import {
  NormalizedBookingInfo,
  NormalizedJoyPlace,
  NormalizedTravelReservationBookingDetailsQuery,
  TravelReservationBookingInfo,
  TravelReservationJoyPlace,
} from '../components/GuestHotelBookingsList/GuestHotelBookingsList.types';

export const AccommodationsProvider: React.FC<AccommodationsContextProps> = ({ children, componentData, eventHandle, containerId, pageSlug, pageConfig }) => {
  const { venueInfo, loading: loadingEventPlaces } = useEventPlaces();
  const { eventInfo } = useEventInfo();
  const { eventId } = eventInfo || {};

  const { isSuperAdmin } = useAuth();
  const { data: accommodationsData, loading: loadingAccommodations } = useAccommodationHotelsQuery({
    variables: { name: eventHandle || '' },
    batchMode: 'fast',
  });

  const { data: bookingDetailsData, loading: loadingBookingDetails } = useTravelReservationBookingDetailsQuery({
    variables: {
      payload: { eventId: eventId! },
    },
    batchMode: 'fast',
    skip: !eventId,
  });

  // Normalize booking details data with fallback
  const normalizedBookingDetailsData = useMemo(() => {
    if (!bookingDetailsData) return undefined;

    return {
      travelReservationBookingDetails: normalizeWithFallback<TravelReservationBookingDetailsQuery, NormalizedTravelReservationBookingDetailsQuery>(
        bookingDetailsData,
        bookingReservationFallback,
      ).travelReservationBookingDetails.map(booking => {
        return {
          bookingInfo: booking.bookingInfo.map(bookingInfo => normalizeWithFallback<TravelReservationBookingInfo, NormalizedBookingInfo>(bookingInfo, bookingInfoFallback)),
          joyPlace: normalizeWithFallback<TravelReservationJoyPlace, NormalizedJoyPlace>(booking.joyPlace || {}, joyPlaceFallback),
        };
      }),
    };
  }, [bookingDetailsData]);

  const accommodationPayloadToFetchPhotos = useMemo(
    () =>
      accommodationsData?.eventByName?.accommodationPlacesV2
        ?.map(place => {
          if (place.type === AccommodationPlaceType.hotel) {
            return place.joyPlaceId;
          }
          return null;
        })
        .filter(Boolean) as string[],
    [accommodationsData?.eventByName?.accommodationPlacesV2],
  );

  const { accommodationPropertyPhotos, loadingAccommodationPropertyPhotos } = useAccommodationPlacePhotos(accommodationPayloadToFetchPhotos);

  return (
    <AccommodationsContext.Provider
      value={{
        componentData,
        eventHandle,
        containerId,
        pageSlug,
        accommodationsData: accommodationsData?.eventByName || undefined,
        venueData: venueInfo,
        loadingData: loadingAccommodations || loadingEventPlaces || loadingAccommodationPropertyPhotos || loadingBookingDetails,
        isSuperAdmin,
        accommodationPropertyPhotos,
        pageConfig,
        bookingDetailsData: normalizedBookingDetailsData,
        loadingBookingDetails,
      }}
    >
      {children}
    </AccommodationsContext.Provider>
  );
};
